import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { type ReasonScope, reasonChoice, reasonsQuery } from "../../reasons";

/**
 * The reason for a cancel, suspend or reroute (P15-14): one of the company's reasons for that
 * status (Settings › Status reasons) and a note. With no scope, or no reasons set up for it, the
 * note alone is the reason.
 */
export function useReasonPicker(scope: ReasonScope | undefined) {
  const reasons = useQuery({ ...reasonsQuery(scope), enabled: scope !== undefined });
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const list = scope ? (reasons.data ?? []) : [];
  const choice = reasonChoice(list, selected, note);
  const loading = scope !== undefined && reasons.isPending;
  return {
    list,
    selected,
    setSelected,
    note,
    setNote,
    ...choice,
    missing: loading ? "Loading the reasons…" : choice.missing,
  };
}
export type ReasonPicker = ReturnType<typeof useReasonPicker>;

/** The picker and the note. `showMissing` once the form was submitted with something missing. */
export function ReasonFields({
  picker,
  showMissing,
  rows = 3,
}: {
  picker: ReasonPicker;
  showMissing: boolean;
  rows?: number;
}): ReactNode {
  const structured = picker.list.length > 0;
  const missing = showMissing ? picker.missing : undefined;
  const pickMissing = structured && !picker.reasonId;
  return (
    <>
      {structured ? (
        <Select
          label="Reason"
          placeholder="Choose a reason"
          value={picker.selected}
          onValueChange={picker.setSelected}
          options={picker.list.map((r) => ({ value: r.id, label: r.name }))}
          description={pickMissing ? missing : undefined}
        />
      ) : null}
      <Textarea
        label={structured ? "Note" : "Reason"}
        description={structured ? "Optional, unless the reason is Other. It goes on the timeline." : undefined}
        rows={rows}
        value={picker.note}
        onChange={(e) => picker.setNote(e.target.value)}
        error={pickMissing ? undefined : missing}
      />
    </>
  );
}
